(() => {
  'use strict';

  const configElement = document.getElementById('SmibSiteConfig');
  const csvStoragePrefix = 'smib-csv-cache:v3:';
  const csvMemory = new Map();
  const csvPending = new Map();
  let youtubeApiPromise;

  function readConfig() {
    const fallback = {
      csv: { preload: true, ttlMinutes: 60, rawUrls: '' },
      playlists: { preload: true, rawUrls: '' },
      klaviyo: { enabled: true, companyId: 'XG5MJy', defaultListId: 'RdhN3m' },
    };

    if (!configElement) return fallback;

    try {
      const parsed = JSON.parse(configElement.textContent || '{}');
      return {
        csv: {
          preload: parsed.csv?.preload !== false,
          ttlMinutes: Math.max(0, Number(parsed.csv?.ttlMinutes) || 60),
          rawUrls: typeof parsed.csv?.rawUrls === 'string' ? parsed.csv.rawUrls : '',
        },
        playlists: {
          preload: parsed.playlists?.preload !== false,
          rawUrls: typeof parsed.playlists?.rawUrls === 'string' ? parsed.playlists.rawUrls : '',
        },
        klaviyo: {
          enabled: parsed.klaviyo?.enabled !== false,
          companyId: String(parsed.klaviyo?.companyId || 'XG5MJy').trim(),
          defaultListId: String(parsed.klaviyo?.defaultListId || 'RdhN3m').trim(),
        },
      };
    } catch (error) {
      console.warn('[SMIB] Centrale instellingen konden niet worden gelezen.', error);
      return fallback;
    }
  }

  function parseLines(rawValue) {
    const seen = new Set();

    return String(rawValue || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .filter(line => {
        if (seen.has(line)) return false;
        seen.add(line);
        return true;
      });
  }

  function normalizeCsvUrl(value) {
    const url = String(value || '').trim();
    if (!url) return '';
    return url.includes('/pubhtml') ? url.replace('/pubhtml', '/pub?output=csv') : url;
  }

  function getCsvStorageKey(url) {
    return `${csvStoragePrefix}${encodeURIComponent(url)}`;
  }

  function readStoredCsv(url, maxAge) {
    try {
      const raw = localStorage.getItem(getCsvStorageKey(url));
      if (!raw) return null;
      const entry = JSON.parse(raw);

      if (
        entry &&
        typeof entry.text === 'string' &&
        Number.isFinite(entry.savedAt) &&
        Date.now() - entry.savedAt < maxAge
      ) {
        return entry;
      }

      localStorage.removeItem(getCsvStorageKey(url));
    } catch (error) {
      console.warn('[SMIB CSV] Browsercache kon niet worden gelezen.', error);
    }

    return null;
  }

  function storeCsv(url, entry) {
    csvMemory.set(url, entry);
    try {
      localStorage.setItem(getCsvStorageKey(url), JSON.stringify(entry));
    } catch (error) {
      console.warn('[SMIB CSV] Browsercache kon niet worden opgeslagen.', error);
    }
  }

  function parseYouTube(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    if (/^[A-Za-z0-9_-]{10,}$/.test(raw) && !raw.includes('.')) {
      return {
        provider: 'youtube',
        type: 'playlist',
        id: raw,
        url: `https://www.youtube.com/playlist?list=${encodeURIComponent(raw)}`,
        embedUrl: `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(raw)}&rel=0`,
      };
    }

    try {
      const url = new URL(raw);
      const host = url.hostname.replace(/^www\./, '');
      if (!['youtube.com', 'm.youtube.com', 'youtu.be', 'music.youtube.com'].includes(host)) return null;
      const playlistId = url.searchParams.get('list');
      if (!playlistId) return null;

      return {
        provider: 'youtube',
        type: 'playlist',
        id: playlistId,
        url: raw,
        embedUrl: `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(playlistId)}&rel=0`,
      };
    } catch (_error) {
      return null;
    }
  }

  function parseSpotify(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const uriMatch = raw.match(/^spotify:(playlist|album|show|episode|artist):([A-Za-z0-9]+)$/i);
    if (uriMatch) {
      const type = uriMatch[1].toLowerCase();
      const id = uriMatch[2];
      return {
        provider: 'spotify',
        type,
        id,
        url: `https://open.spotify.com/${type}/${id}`,
        embedUrl: `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`,
      };
    }

    try {
      const url = new URL(raw);
      if (url.hostname.replace(/^www\./, '') !== 'open.spotify.com') return null;
      const parts = url.pathname.split('/').filter(Boolean);
      const typeIndex = parts.findIndex(part => ['playlist', 'album', 'show', 'episode', 'artist'].includes(part));
      if (typeIndex < 0 || !parts[typeIndex + 1]) return null;
      const type = parts[typeIndex];
      const id = parts[typeIndex + 1];

      return {
        provider: 'spotify',
        type,
        id,
        url: raw,
        embedUrl: `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`,
      };
    } catch (_error) {
      return null;
    }
  }

  function identifyPlaylist(value) {
    return parseYouTube(value) || parseSpotify(value) || {
      provider: 'unknown',
      type: 'unknown',
      id: '',
      url: String(value || '').trim(),
      embedUrl: '',
    };
  }

  function loadYouTubeApi() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeApiPromise) return youtubeApiPromise;

    youtubeApiPromise = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      const timeout = window.setTimeout(() => reject(new Error('YouTube API timeout.')), 15000);

      window.onYouTubeIframeAPIReady = () => {
        window.clearTimeout(timeout);
        if (typeof previousReady === 'function') previousReady();
        resolve(window.YT);
      };

      const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
      if (existing) return;

      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('YouTube API kon niet worden geladen.'));
      };
      document.head.appendChild(script);
    });

    return youtubeApiPromise;
  }

  const config = readConfig();
  const csvUrls = parseLines(config.csv.rawUrls).map(normalizeCsvUrl).filter(Boolean);
  const playlistUrls = parseLines(config.playlists.rawUrls);
  const defaultCsvTtl = config.csv.ttlMinutes;

  const csv = {
    urls: Object.freeze([...csvUrls]),
    ready: Promise.resolve([]),

    getUrl(sourceIndex = 1) {
      const index = Math.max(1, Number(sourceIndex) || 1);
      return csvUrls[index - 1] || '';
    },

    get(source, ttlMinutes = defaultCsvTtl) {
      const numericSource = typeof source === 'number' || /^\d+$/.test(String(source || ''));
      const url = numericSource ? this.getUrl(Number(source)) : normalizeCsvUrl(source);

      if (!url) return Promise.reject(new Error('Geen centrale CSV-bron ingesteld.'));

      const maxAge = Math.max(0, Number(ttlMinutes) || defaultCsvTtl) * 60 * 1000;
      const memoryEntry = csvMemory.get(url);
      if (memoryEntry && Date.now() - memoryEntry.savedAt < maxAge) return Promise.resolve(memoryEntry.text);

      const stored = readStoredCsv(url, maxAge);
      if (stored) {
        csvMemory.set(url, stored);
        return Promise.resolve(stored.text);
      }

      if (csvPending.has(url)) return csvPending.get(url);

      const request = fetch(url, { cache: 'force-cache' })
        .then(response => {
          if (!response.ok) throw new Error(`CSV kon niet worden geladen (${response.status}).`);
          return response.text();
        })
        .then(text => {
          storeCsv(url, { text, savedAt: Date.now() });
          return text;
        })
        .finally(() => csvPending.delete(url));

      csvPending.set(url, request);
      return request;
    },

    preload() {
      return Promise.allSettled(csvUrls.map(url => this.get(url)));
    },

    clear(source) {
      const numericSource = typeof source === 'number' || /^\d+$/.test(String(source || ''));
      const url = numericSource ? this.getUrl(Number(source)) : normalizeCsvUrl(source);
      if (!url) return;
      csvMemory.delete(url);
      csvPending.delete(url);
      try {
        localStorage.removeItem(getCsvStorageKey(url));
      } catch (_error) {
        // localStorage may be unavailable in private browsing.
      }
    },

    clearAll() {
      csvUrls.forEach(url => this.clear(url));
    },
  };

  const playlists = {
    urls: Object.freeze([...playlistUrls]),
    sources: Object.freeze(playlistUrls.map(identifyPlaylist)),
    ready: Promise.resolve([]),

    getUrl(sourceIndex = 1) {
      const index = Math.max(1, Number(sourceIndex) || 1);
      return playlistUrls[index - 1] || '';
    },

    get(sourceIndex = 1) {
      const value = typeof sourceIndex === 'number' || /^\d+$/.test(String(sourceIndex || ''))
        ? this.getUrl(Number(sourceIndex))
        : String(sourceIndex || '').trim();
      return identifyPlaylist(value);
    },

    resolve(sourceIndex, fallbackUrl) {
      const centralUrl = this.getUrl(sourceIndex);
      return identifyPlaylist(centralUrl || fallbackUrl || '');
    },

    loadYouTubeApi,

    preload() {
      const jobs = [];
      const sources = this.sources;
      if (sources.some(source => source.provider === 'youtube')) jobs.push(loadYouTubeApi());

      sources
        .filter(source => source.provider === 'spotify' && source.embedUrl)
        .forEach(source => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'document';
          link.href = source.embedUrl;
          document.head.appendChild(link);
        });

      return Promise.allSettled(jobs);
    },
  };

  window.SMIBSiteConfig = config;
  window.SMIBDataSources = Object.freeze({ config, csv, playlists });
  window.SMIBCsvCache = csv;
  window.SMIBPlaylistRegistry = playlists;

  csv.ready = config.csv.preload
    ? csv.preload().then(results => {
        document.dispatchEvent(new CustomEvent('smib:csv-cache-ready', { detail: { results } }));
        return results;
      })
    : Promise.resolve([]);

  playlists.ready = config.playlists.preload
    ? playlists.preload().then(results => {
        document.dispatchEvent(new CustomEvent('smib:playlist-sources-ready', { detail: { results } }));
        return results;
      })
    : Promise.resolve([]);
})();
